import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";

// DraggableItem component
const DraggableItem = ({ item, index, renderItem, className = "" }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: item.id,
    });

  const { setNodeRef: setDroppableRef } = useDroppable({
    id: `droppable-${item.id}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? "none" : "transform 150ms ease",
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : "auto",
  };
  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        setDroppableRef(node);
      }}
      style={style}
      {...attributes}
      className={`flex items-center ${className} ${
        isDragging ? "shadow-lg dragging" : ""
      }`}
      data-index={index}
    >
      <div className="d-flex align-items-center" style={{ gap: "12px" }}>
        <div
          {...listeners}
          className="mr-4 draggable-icon"
          style={{ width: "20px" }}
          onMouseDown={(e) => {
            const target = e.currentTarget;
            target.style.cursor = "grabbing";
          }}
          onMouseUp={(e) => {
            const target = e.currentTarget;
            target.style.cursor = "grab";
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24px"
            height="24px"
            viewBox="0 0 25 25"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.5 8C10.3284 8 11 7.32843 11 6.5C11 5.67157 10.3284 5 9.5 5C8.67157 5 8 5.67157 8 6.5C8 7.32843 8.67157 8 9.5 8ZM9.5 14C10.3284 14 11 13.3284 11 12.5C11 11.6716 10.3284 11 9.5 11C8.67157 11 8 11.6716 8 12.5C8 13.3284 8.67157 14 9.5 14ZM11 18.5C11 19.3284 10.3284 20 9.5 20C8.67157 20 8 19.3284 8 18.5C8 17.6716 8.67157 17 9.5 17C10.3284 17 11 17.6716 11 18.5ZM15.5 8C16.3284 8 17 7.32843 17 6.5C17 5.67157 16.3284 5 15.5 5C14.6716 5 14 5.67157 14 6.5C14 7.32843 14.6716 8 15.5 8ZM17 12.5C17 13.3284 16.3284 14 15.5 14C14.6716 14 14 13.3284 14 12.5C14 11.6716 14.6716 11 15.5 11C16.3284 11 17 11.6716 17 12.5ZM15.5 20C16.3284 20 17 19.3284 17 18.5C17 17.6716 16.3284 17 15.5 17C14.6716 17 14 17.6716 14 18.5C14 19.3284 14.6716 20 15.5 20Z"
              fill="#004e5c"
            />
          </svg>
        </div>
        {renderItem(item, index)}
      </div>
    </div>
  );
};

// DragDropList component
const ReorderableList = ({
  items,
  renderItem,
  containerClassName = "",
  itemClassName = "",
  separator,
  onOrderChange,
}) => {
  // Setup sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeIndex = items.findIndex((item) => item.id === active.id);
    const overIndex = items.findIndex(
      (item) => item.id === over.id.replace("droppable-", "")
    );
    const newItems = [...items];
    const [movedItem] = newItems.splice(activeIndex, 1);
    newItems.splice(overIndex, 0, movedItem);

    onOrderChange(newItems);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className={`overflow-y-auto ${containerClassName}`}>
        {items.map((item, index) => (
          <React.Fragment key={item.id}>
            <DraggableItem
              item={item}
              index={index}
              renderItem={renderItem}
              className={itemClassName}
            />
            <div className="">{separator(item, index)}</div>
          </React.Fragment>
        ))}
      </div>
    </DndContext>
  );
};

export default ReorderableList;
